import { useEffect, useRef, useState } from "react";
import { pollTeaserUntilComplete } from "../services/teaserApi";

/**
 * Drives the on-page teaser reveal: polls the backend and exposes a simple
 * state machine for the result page.
 *
 * phase: 'idle' | 'generating' | 'ready' | 'slow' | 'failed'
 */
export default function useTeaserReveal(requestId) {
  const [phase, setPhase] = useState(requestId ? "generating" : "idle");
  const [teaser, setTeaser] = useState("");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [landingVariant, setLandingVariant] = useState("");
  const [emailStatus, setEmailStatus] = useState("unknown");
  const [birthTimeConfidence, setBirthTimeConfidence] = useState("unknown");
  const [majorPeriods, setMajorPeriods] = useState(null);
  const [focusLinks, setFocusLinks] = useState(null);
  const [offerWindow, setOfferWindow] = useState(null);
  const controllerRef = useRef(null);

  useEffect(() => {
    setTeaser("");
    setElapsedMs(0);
    setLandingVariant("");
    setEmailStatus("unknown");
    setMajorPeriods(null);
    setFocusLinks(null);
    if (!requestId) {
      setPhase("idle");
      return undefined;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setPhase("generating");

    pollTeaserUntilComplete(requestId, {
      signal: controller.signal,
      onProgress: (p) => setElapsedMs(p.elapsedMs || 0),
    })
      .then((result) => {
        if (controller.signal.aborted) return;
        setLandingVariant(result.landingVariant || "");
        setEmailStatus(result.emailStatus || "unknown");
        setBirthTimeConfidence(result.birthTimeConfidence || "unknown");

        if (result.status === "DONE" && (result.teaser || "").trim()) {
          setTeaser(result.teaser);
          setMajorPeriods(result.majorPeriods || null);
          setFocusLinks(result.focusLinks || null);
          setOfferWindow(result.offerWindow || null);
          setPhase("ready");
        } else if (result.status === "FAILED") {
          setPhase("failed");
        } else if (result.status === "TIMEOUT") {
          setPhase("slow");
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setPhase("slow");
      });

    return () => controller.abort();
  }, [requestId]);

  return { phase, teaser, elapsedMs, landingVariant, emailStatus, birthTimeConfidence, majorPeriods, focusLinks, offerWindow };
}
